// Account — the tenant. One per GitHub user OR org. Tier governs billing
// and repo-count caps; onboardingState drives the dashboard funnel.

import { z } from 'zod';
import { AccountIdSchema } from '../value-objects/ids';
import { RegionCodeSchema } from '../value-objects/region-code';

export const AccountKind = {
  User: 'user',
  Org: 'org',
} as const;
export const AccountKindSchema = z.enum(AccountKind);
export type AccountKind = z.infer<typeof AccountKindSchema>;

/**
 * An ORG account with no box budget at all — the ONE statement of the rule behind
 * the api's `org_unfunded` refusal (`enrollmentVerdict`,
 * `apps/api/src/deps/enrollment-capacity.ts`) and the dashboard's up-front Fleet
 * notice (`apps/dashboard`). Both used to spell it; two spellings drift (#3737).
 *
 * The free invite bundle is a user-subject `ai_devs` grant that lifts a person's
 * PERSONAL account only (`grantedAiDevsFor`, `@developerz/billing`), so this is
 * the state a person reaches by running `box add` on their org — named apart from
 * `seat_cap` because the fix differs: seats for THIS org, or an account-subject
 * grant. A personal account at 0 is an ordinary empty fleet, never this.
 *
 * `accountKind` is a plain string because the dashboard reads it off the wire
 * (`Me.kind`). Only `AccountKind.Org` answers true: `null` (the api's
 * no-seat-row snapshot), an absent field, or a kind this build does not know all
 * answer false — an unknown kind is not evidence the account is an org.
 */
export function isOrgUnfunded(input: {
  accountKind: string | null | undefined;
  budget: number;
}): boolean {
  return input.accountKind === AccountKind.Org && input.budget === 0;
}

// Operational account class (not pricing math). Usage billing ($1/handled unit,
// #137) replaced the old repo-count tiers. Platform pricing (arc 101) adds `pro`
// — the commercial seat tier — and `oss_donor`, the $50/mo donor tier (BYOVM +
// BYOK; its runners work OSS queues only). `solo` (#444) is the commercial seat
// tier for solo developers — same entitlement shape as `pro`, only the Stripe
// price shape differs. The pre-arc-101 `paid` tier (migrated to `pro` by 0036)
// and the never-priced `company` seat tier (#1254) were deleted once prod held
// zero rows of either (census 2026-09-27). `Tier` classifies waiver + fairness /
// cost caps: free (personal) · solo · pro (seats) · oss_verified (waived) ·
// oss_donor (donor) · enterprise (top caps).
export const Tier = {
  Free: 'free',
  Solo: 'solo',
  Pro: 'pro',
  OssVerified: 'oss_verified',
  OssDonor: 'oss_donor',
  Enterprise: 'enterprise',
} as const;
export const TierSchema = z.enum(Tier);
export type Tier = z.infer<typeof TierSchema>;

/**
 * The account's maintenance POSTURE (plan 102 slice 06, #7545) — the stored
 * `accounts.maintain_posture` value, whose CHECK spells exactly these two.
 * `standard` is today's behaviour: a repo with no committed policy resolves
 * `defaultPolicy`. `maintain` serves every installed repo: a policy-less repo
 * resolves `@developerz/policy`'s `defaultPolicyFor('maintain')` instead.
 * A text enum, not a boolean, so a later posture is a CHECK change rather than a
 * column swap. Declared HERE because `@developerz/db` may depend on domain only;
 * `@developerz/policy` re-exports it beside `defaultPolicyFor`.
 */
export const AccountPosture = {
  Standard: 'standard',
  Maintain: 'maintain',
} as const;
export const AccountPostureSchema = z.enum(AccountPosture);
export type AccountPosture = z.infer<typeof AccountPostureSchema>;

/**
 * A manual tier / seats write refused because a Stripe seat subscription owns the
 * account (#5725): the next `customer.subscription.*` webhook would silently revert
 * it. Thrown by every seam that would otherwise write over the subscription — the
 * operator's `set_tier` / `set_seats` port and the OSS approve inside
 * `decideOssVerificationRequest` (rolling its verdict back) — and answered as a
 * refusal by each door: the MCP `platform` verb's `stripe_managed` body, a 409 on
 * REST. It lives HERE, not in `@developerz/mcp`, because `@developerz/db` throws it
 * and may not import mcp.
 */
export class StripeManagedOverride extends Error {
  readonly subscription_id: string;
  constructor(subscription_id: string) {
    super(
      `account is billed through Stripe (subscription ${subscription_id}); change the subscription, or issue an \`ai_devs\` grant`,
    );
    this.name = 'StripeManagedOverride';
    this.subscription_id = subscription_id;
  }
}

declare const SERVED_TIER: unique symbol;
/**
 * The tier an account is SERVED as — what every capability gate reads (private-repo
 * enable, the free-tier session and agent-hour caps, the auto-merge opt-in). Equal to
 * the billing `Tier` except that a non-fleet tier (`free`, `oss_verified`) holding an
 * operator-granted seat (`ai_devs` / `unlimited_fleet`) is served as `solo`: a comped
 * user gets the solo product, free, while `accounts.tier` keeps saying who pays.
 *
 * BRANDED so a raw `accounts.tier` cannot reach a gate: the only constructor is
 * `servedTierOf` (`@developerz/billing`), which reads the grants. A gate typed on
 * `Tier` would compile with the grants silently dropped — exactly how a comped user
 * kept hitting the free caps. Tests build one through `servedTierOf` or a local cast.
 */
export type ServedTier = Tier & { readonly [SERVED_TIER]: true };

/**
 * What an account's tier entitles OUR review verdict to DO — the ceiling above
 * whatever the repo's own `.maintainer.yml` asks for:
 *
 * | mode | meaning |
 * |------|---------|
 * | `off` | the native reviewer is not part of this tier; no review is enqueued |
 * | `comments` | advisory — findings + sticky status, and NEVER a merge-gating event |
 * | `full` | the repo's `approval` / `request_changes` knobs decide, up to a real APPROVE |
 *
 * Lives here rather than in `@developerz/billing` (which computes it) because the
 * enforcement points are downstream of billing in the dependency graph: the task
 * ENVELOPE carries it to a box, and `@developerz/review` is what reads it. Effective
 * behaviour is the INTERSECTION with the repo config — a repo may always be quieter
 * than its entitlement, never louder.
 */
export const ReviewMode = {
  Off: 'off',
  Comments: 'comments',
  Full: 'full',
} as const;
/**
 * The full ladder, including `off` — what `entitlements()` RESOLVES for an
 * account. Use {@link DispatchableReviewModeSchema} for anything a review task
 * may carry: `off` is a legal entitlement but never a legal envelope stamp.
 */
export const ReviewModeSchema = z.enum(ReviewMode);
export type ReviewMode = z.infer<typeof ReviewModeSchema>;

/**
 * The modes a DISPATCHED review may carry — `off` excluded, structurally.
 *
 * An `off` account gets no review task at all, so an envelope stamped `off` is a
 * contradiction: `reviewEventFor` would only downgrade the posted EVENT to a
 * comment, while the box still ran the review the tier does not include. Refusing
 * it at the parse boundary is what makes "off never reaches the envelope" a
 * property of the schema rather than a claim in a comment — the defect class
 * #1523 exists to close, so it must not be reintroduced by this fix.
 */
export const DispatchableReviewModeSchema = z.enum([ReviewMode.Comments, ReviewMode.Full]);
export type DispatchableReviewMode = z.infer<typeof DispatchableReviewModeSchema>;

/**
 * The tiers a caller may BUY themselves — the checkout vocabulary, and the plan
 * pick the onboarding funnel offers. Deliberately narrower than `TierSchema`:
 * `enterprise` is a custom contract with no price
 * object, `oss_donor` rides the donation flow, and `free`/`oss_verified`
 * never bill — a tier with no price here would be a promise checkout cannot keep.
 *
 * It lives in `domain` because THREE surfaces spell it: the REST checkout body
 * and the onboarding advance body (`apps/api`), and the MCP `billing.checkout` /
 * `config.onboarding_advance` actions (`packages/mcp`, which may not import an
 * app). Three copies of a price-backed enum is how one of them ends up offering
 * a tier nothing can charge for.
 */
export const SELF_SERVE_SEAT_TIERS = [Tier.Solo, Tier.Pro] as const;
export const SelfServeSeatTierSchema = z.enum(SELF_SERVE_SEAT_TIERS);
export type SelfServeSeatTier = z.infer<typeof SelfServeSeatTierSchema>;

/**
 * Upper bound for one self-serve seat cart — a footgun guard (a fat-fingered
 * `500` on a $500-per-AI-dev price is a $250k/mo subscription), not a pricing
 * policy. Fleets past this buy through enterprise. In `domain` for the reason
 * {@link SELF_SERVE_SEAT_TIERS} is: the REST checkout body (`apps/api`), the MCP
 * `billing.checkout` action (`packages/mcp`, which may not import an app) and
 * the dashboard's seat input all cite ONE number instead of three literal `100`s.
 */
export const MAX_CHECKOUT_SEATS = 100;

// Funnel order (arc 101 slice 02, box + MCP rungs #3632): New → Authed →
// PlanSelected → KeySet → GhConnected → BoxAttached → ReposSelected → YmlRequested →
// McpConnected → Active (→ Invalid). `plan_selected` (single/team pick) + the BYOK
// key step precede the GitHub App install; `box_attached` sits before the repo pick
// because the setup pass that mints the `.maintainer.yml` PR runs on the customer's
// box, and `mcp_connected` sits before `active` because a tenant with no chat client
// connected cannot drive the product it just finished onboarding onto. The
// authoritative transition table lives in `../state-machines/onboarding.ts`.
export const OnboardingState = {
  New: 'new',
  Authed: 'authed',
  PlanSelected: 'plan_selected',
  KeySet: 'key_set',
  GhConnected: 'gh_connected',
  BoxAttached: 'box_attached',
  ReposSelected: 'repos_selected',
  YmlRequested: 'yml_requested',
  McpConnected: 'mcp_connected',
  Active: 'active',
  Invalid: 'invalid',
} as const;
export const OnboardingStateSchema = z.enum(OnboardingState);
export type OnboardingState = z.infer<typeof OnboardingStateSchema>;

export const AccountSchema = z.object({
  id: AccountIdSchema,
  kind: AccountKindSchema,
  /**
   * The numeric GitHub `id` (`accounts.github_account_id`). Immutable on GitHub's
   * side — a renamed org keeps the same id, a freed handle re-registered by a new
   * account gets a different one. Platform-account gates (#5443) key on this, not
   * on `githubLogin`. Optional in fixtures that predate the column.
   */
  githubAccountId: z.number().int().positive().nullable().optional(),
  githubLogin: z.string().min(1).nullable(),
  email: z.string().email().nullable(),
  tier: TierSchema,
  onboardingState: OnboardingStateSchema,
  /** Per-account monthly spend cap, USD. Null = use the default cap (#156). */
  spendCapUsd: z.number().nonnegative().nullable(),
  verifiedOssAt: z.date().nullable(),
  createdAt: z.date(),
});
export type Account = z.infer<typeof AccountSchema>;

// There is deliberately NO `tierAllowedForKind` here any more (deleted 2026-08-09).
//
// It claimed `free` was legal only for `kind: 'user'` and cited two sentences:
// `docs/idea/domain.md`'s "`Account.tier = 'free'` ⇒ `account.kind = 'user'`" —
// which that file does not say, it states the CONVERSE (`kind='user'` ⇒ free, a
// METERING waiver) — and a billing.md line, "Free (personal) — unlimited under
// kind=user GitHub accounts", which commit ca711756 (#150, the usage-based money
// model) deleted on 2026-06-26. Both citations were dead.
//
// **Tier is a repo-VISIBILITY line, not an account-kind line.** `billing.md`
// prices `free` at $0 for "any of the account's public repos" and says outright
// "Public vs. private is a per-repo line, not a tier line"; `EntitlementInput`
// and `RepoEnableInput` (`@developerz/billing`) carry no `kind` field at all, so
// an org on `free` is byte-identical to a user on `free` everywhere tier is read.
//
// It was also unenforceable in the direction that mattered. Six writers produce
// `org` + `free` — both account-creating upserts, the seed, and
// `apps/worker/src/consumers/stripe-events.ts`, which writes `Tier.Free` on every
// non-seat-granting subscription regardless of kind. Only ONE consulted the
// invariant: the admin `set_tier` verb. So its single live effect was inverted —
// it refused an operator the exact state Stripe puts a cancelled org in.
//
// If you are about to re-add a kind→tier rule: it needs a $0 tier an org may
// hold (`oss_verified` is not one — it requires an admin-set `verified_ossAt`),
// and it needs every writer above, not one seam. Guarded by
// `apps/api/src/deps/mcp-ports.integration.test.ts`.

/**
 * Invariant: `tier='oss_verified'` requires `verifiedOssAt` set by admin review.
 * The paid donor tier `oss_donor` is a payment tier, NOT the free-waiver review
 * tier, so it carries no `verifiedOssAt` requirement.
 */
export function ossVerifiedConsistent(account: Account): boolean {
  if (account.tier === Tier.OssVerified) return account.verifiedOssAt !== null;
  return true;
}

// ---------------------------------------------------------------------------
// Identity (slice 02, arc 101) — users + memberships.
//
// An `Account` (above) is the *tenant* — a personal namespace or an org. A
// `User` is the *person*, who reaches many accounts through `Membership` rows
// (GitHub-style multi-org). Identity is anchored on `githubUserId` (stable
// across login/email renames); an SSO login is a `user_identities` row (#466).
// Mirrors the `users` / `memberships` tables in `@developerz/db`
// (`packages/db/src/schema.ts`).
// ---------------------------------------------------------------------------

/**
 * A person's role within one account, ordered by privilege (`owner` ≫ `viewer`).
 * The API middleware maps each to a scope ceiling (viewer = read:*, member +=
 * write:tasks, admin += write:runners/billing, owner = all; slice 02 PR4).
 * Stored as text and validated here at read — no pgEnum, same stance as `tier`.
 */
export const Role = {
  Owner: 'owner',
  Admin: 'admin',
  Member: 'member',
  Viewer: 'viewer',
} as const;
export const RoleSchema = z.enum(Role);
export type Role = z.infer<typeof RoleSchema>;

/**
 * A human. Distinct from `Account`: a user belongs to many accounts through
 * `Membership`. `githubUserId` is the stable identity anchor (survives login /
 * email renames); `login`/`email`/`avatar` are mutable profile fields.
 */
export const UserSchema = z.object({
  /** Raw uuid PK — no brand (the id is a DB handle, not a domain value object). */
  id: z.string().uuid(),
  /**
   * Stable GitHub numeric user id — survives login/email renames. Unique when
   * set; null for users provisioned JIT from a first-party OIDC issuer (the
   * "Login with Tesote" leg), who have no GitHub identity and are anchored on
   * their `user_identities` rows instead (#466).
   */
  githubUserId: z.number().int().positive().nullable(),
  /** GitHub login (handle). Mutable — never a key; `githubUserId` is. */
  login: z.string().min(1),
  /** Primary email; nullable — GitHub may withhold it. */
  email: z.string().email().nullable(),
  /** Avatar URL; nullable. */
  avatar: z.string().url().nullable(),
  /**
   * Closed-beta approval gate. Null = registered but waitlisted — the API
   * middleware allows only `read:account` until an admin approves (slice 02).
   */
  approvedAt: z.date().nullable(),
  /**
   * ToS/privacy version this user last accepted (#554). Null = never accepted;
   * the dashboard blocks on a consent screen until it equals {@link TOS_VERSION}.
   * A version bump re-prompts (accepted !== current) — that's the whole re-consent
   * mechanism, no separate flag.
   */
  tosAcceptedVersion: z.string().min(1).nullable(),
  /** When {@link tosAcceptedVersion} was accepted; null when never (#554). */
  tosAcceptedAt: z.date().nullable(),
  /**
   * The region the person DECLARED (#5881), ISO 3166-1 alpha-2. Null = never
   * asked; the dashboard asks once. `CN` (mainland China) owes the separate
   * cross-border consent (`missingConsent`, `./user-consent.ts`); a null region
   * is never capped.
   */
  region: RegionCodeSchema.nullable(),
  createdAt: z.date(),
});
export type User = z.infer<typeof UserSchema>;

/**
 * Current Terms-of-Service / privacy version (#554). A user whose
 * `tosAcceptedVersion` differs is re-prompted for consent before any account
 * access. Bump this string (date-stamped) whenever the ToS/privacy text changes.
 */
export const TOS_VERSION = '2026-07-14';

/**
 * Current version of the cross-border transfer notice a `CN` user consents to
 * (#5881, PIPL Art. 38-39; `./user-consent.ts`). Bumping it RE-ASKS EVERY `CN`
 * USER: a stored consent counts only while its `version` equals this string
 * (`missingConsent`), so each one is capped to `read:account` until they grant
 * the new notice. Bump it (date-stamped) whenever the notice's recipients,
 * purposes or rights text changes, and only then.
 */
export const CROSS_BORDER_CONSENT_VERSION = '2026-10-04';

/**
 * A (user, account) pair with a role — one row per pair, so a user has exactly
 * one role per account. Login-time installation sync upserts one membership per
 * account the user can reach; the identity migration seeds an `owner` membership
 * for each legacy user-kind account.
 */
export const MembershipSchema = z.object({
  /** Raw uuid PK — no brand (the id is a DB handle, not a domain value object). */
  id: z.string().uuid(),
  /** The person (`users.id`). */
  userId: z.string().uuid(),
  /** The tenant this membership grants access to (`accounts.id`). */
  accountId: AccountIdSchema,
  role: RoleSchema,
  createdAt: z.date(),
});
export type Membership = z.infer<typeof MembershipSchema>;

// ─── member-management RBAC (#308) ───────────────────────────────────────────
//
// Pure policy for the org member-management verbs (invite / change-role /
// remove). The route already gated the caller on `write:members` (owner or
// admin); this refines WHO they may act on, WHAT role they may grant, and
// guards the last owner. No I/O — the caller supplies the current owner count.

/** Privilege rank — higher manages lower. owner 3 ▸ admin 2 ▸ member 1 ▸ viewer 0. */
export function roleRank(role: Role): number {
  return { [Role.Owner]: 3, [Role.Admin]: 2, [Role.Member]: 1, [Role.Viewer]: 0 }[role];
}

export type MemberActionInput = {
  /** The acting caller's role in this org. */
  actorRole: Role;
  /** Whether the target is the caller themselves (self-service leave / demote). */
  isSelf: boolean;
  /** The target's current role, or null when adding a brand-new member. */
  targetCurrentRole: Role | null;
  /** The role to grant (add / change), or null for a remove. */
  desiredRole: Role | null;
  /** How many owners the org has right now — the last-owner guard reads this. */
  ownerCount: number;
};

export type MemberActionResult = { ok: true } | { ok: false; reason: string };

/**
 * Decide whether `actorRole` may add/change/remove a member. Rules:
 *  1. Authority: you may always act on yourself; otherwise an owner may act on
 *     anyone, a non-owner only on someone strictly below their own rank (so an
 *     admin can't touch another admin or an owner).
 *  2. No escalation: the granted role can't exceed the actor's own — an owner
 *     may grant up to owner, a non-owner only strictly below itself (admins
 *     can't mint admins or owners).
 *  3. Last owner: removing or demoting the sole remaining owner is refused, so
 *     an org is never left ownerless.
 */
export function canManageMember(i: MemberActionInput): MemberActionResult {
  const actor = roleRank(i.actorRole);
  const isOwner = i.actorRole === Role.Owner;

  if (i.targetCurrentRole !== null && !i.isSelf) {
    const hasAuthority = isOwner || actor > roleRank(i.targetCurrentRole);
    if (!hasAuthority) return { ok: false, reason: 'insufficient role to manage this member' };
  }

  if (i.desiredRole !== null) {
    const ceiling = isOwner ? actor : actor - 1;
    if (roleRank(i.desiredRole) > ceiling) {
      return { ok: false, reason: 'cannot grant a role above your own' };
    }
  }

  if (demotesLastOwner(i)) {
    return { ok: false, reason: 'cannot remove or demote the last owner' };
  }

  return { ok: true };
}

/**
 * Rule 3 of {@link canManageMember} on its own: does this act remove or demote
 * the org's sole remaining owner? Split out because it is the ONE rule that also
 * binds platform staff, who hold no role in the org (`changeMemberRole`'s
 * `platform_staff` actor, #5727) — one predicate, so the two callers cannot
 * disagree about what "last owner" means.
 */
export function demotesLastOwner(
  i: Pick<MemberActionInput, 'targetCurrentRole' | 'desiredRole' | 'ownerCount'>,
): boolean {
  return i.targetCurrentRole === Role.Owner && i.desiredRole !== Role.Owner && i.ownerCount <= 1;
}
